import { useState, useCallback } from 'react';
import { message } from 'antd';
import { posService } from '@/services/pos.service';
import type { OrderItem, FulfillmentMode } from '../types';
import type { StalePricePreview } from './useStalePricePreview';

interface UseRefreshStalePricesOpts {
  orderItems: OrderItem[];
  staleItemIds: Set<string>;
  updateItem: (id: string, patch: Partial<OrderItem>) => void;
  fulfillmentMode: FulfillmentMode;
  /** If provided and covers every stale item, refresh() applies it directly
   *  without an extra BE round-trip. Falls back to a fresh fetch otherwise. */
  preview?: Map<string, StalePricePreview>;
}

interface UseRefreshStalePricesResult {
  loading: boolean;
  refresh: () => Promise<void>;
}

function recalcTotals(
  item: OrderItem,
  newPrice: number,
  fulfillmentMode: FulfillmentMode,
): { discountAmount: number; totalAmount: number } {
  const isQuantityMode = item.trackingMode === 'quantity' || fulfillmentMode === 'PREORDER';
  const discPerUnit = (newPrice * item.discountPercent) / 100;
  const discountAmount = isQuantityMode
    ? Math.round(discPerUnit * item.quantity)
    : Math.round(discPerUnit);
  const totalAmount = isQuantityMode
    ? newPrice * item.quantity - discountAmount
    : newPrice - discountAmount;
  return { discountAmount, totalAmount };
}

export function useRefreshStalePrices(
  opts: UseRefreshStalePricesOpts,
): UseRefreshStalePricesResult {
  const { orderItems, staleItemIds, updateItem, fulfillmentMode, preview } = opts;
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (staleItemIds.size === 0) return;

    const staleItems = orderItems.filter((it) => staleItemIds.has(it.id));
    if (staleItems.length === 0) return;

    // Fast path: caller supplied a preview map covering every stale item →
    // apply the preview directly without a second BE round-trip.
    if (preview && preview.size > 0 && staleItems.every((it) => preview.has(it.id))) {
      for (const item of staleItems) {
        const p = preview.get(item.id);
        if (!p) continue;
        const { discountAmount, totalAmount } = recalcTotals(item, p.price, fulfillmentMode);
        updateItem(item.id, {
          price: p.price,
          discountAmount,
          totalAmount,
          instrumentCode: p.instrumentCode ?? item.instrumentCode,
          appliedBid: p.appliedBid,
          appliedAsk: p.appliedAsk,
          appliedTs: p.appliedTs,
          appliedOverride: p.appliedOverride,
        });
      }
      void message.success('Đã cập nhật giá theo thị trường');
      return;
    }

    const qrCodes = staleItems.map((it) => it.specificationCode ?? it.variantCode);

    setLoading(true);
    try {
      const res = await posService.lookupVariants(qrCodes, fulfillmentMode);
      for (const detail of res.items) {
        const serialFromBE = detail.stock_serial?.serial;
        const variantCodeFromBE = detail.variant.variant_code;
        const matchedItem = staleItems.find((s) => {
          if (serialFromBE && s.specificationCode === serialFromBE) return true;
          if (!serialFromBE && !s.specificationCode && s.variantCode === variantCodeFromBE) return true;
          return false;
        });
        if (!matchedItem) continue;

        const newPrice = detail.price.applied_price ?? detail.price.price;
        const { discountAmount, totalAmount } = recalcTotals(matchedItem, newPrice, fulfillmentMode);

        updateItem(matchedItem.id, {
          price: newPrice,
          discountAmount,
          totalAmount,
          instrumentCode: detail.price.instrument_code ?? matchedItem.instrumentCode,
          appliedBid: detail.price.applied_bid,
          appliedAsk: detail.price.applied_ask,
          appliedTs: detail.price.applied_ts,
          appliedOverride: detail.price.override_applied,
        });
      }
      void message.success('Đã cập nhật giá theo thị trường');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Không thể cập nhật giá';
      void message.error(msg);
    } finally {
      setLoading(false);
    }
  }, [orderItems, staleItemIds, updateItem, fulfillmentMode, preview]);

  return { loading, refresh };
}
